import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
	fetchDiagnostiques,
	fetchDiagTarifs,
} from "../../../store/actions/diagnostiqueActions"
import { fetchAutreEntites } from "../../../store/actions/autreActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { tableStyles } from "../../styles/tableStyles"
import Loader from "../../basics/Loader"
import DiagnostiqueModalV2 from "./DiagnostiqueModalV2"

// Géométrie des branches : chaque niveau occupe UNIT px, le trait vertical
// passe à LIGNE_X px du bord de son unité (il doit rester continu d'une ligne
// de tableau à l'autre → les unités s'étirent sur toute la hauteur du <td>).
const UNIT = 22
const LIGNE_X = 10
const TRAIT = `1px solid ${COLORS.borderStrong}`

const styles = {
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 16,
	},
	title: { fontSize: 15, fontWeight: 600, color: COLORS.text },
	count: { fontSize: 13, color: COLORS.textLight },
	actions: { display: "flex", gap: 8 },
	btnPlier: {
		...gs.btnSecondary,
		padding: "8px 14px",
		fontSize: 13,
	},
	empty: {
		...tableStyles.empty,
		padding: "40px 16px",
		color: COLORS.textLight,
		fontSize: 14,
	},
	racine: { fontWeight: 600 },
	categorie: { fontWeight: 500 },
	badge: {
		fontSize: 11,
		padding: "2px 8px",
		borderRadius: 99,
		background: COLORS.accentLight,
		color: COLORS.accent,
		marginLeft: 6,
	},
	badgeArticles: {
		fontSize: 11,
		padding: "2px 8px",
		borderRadius: 99,
		background: COLORS.dangerLight,
		color: COLORS.danger,
		marginLeft: 6,
	},
	badgeSurPlace: {
		fontSize: 11,
		padding: "2px 8px",
		borderRadius: 99,
		background: COLORS.successLight,
		color: COLORS.success,
		marginLeft: 6,
	},
	// Cellule "Nom" : padding vertical à 0 pour que les traits touchent les
	// bords de la ligne (continuité des branches entre les lignes).
	arbreCell: { ...tableStyles.td, padding: "0 16px 0 12px" },
	arbreLigne: { display: "flex", alignItems: "center", minHeight: 44 },
	unit: {
		position: "relative",
		width: UNIT,
		alignSelf: "stretch",
		flexShrink: 0,
	},
	// │ — un frère suit plus haut dans l'arbre : le trait traverse la ligne.
	traverse: {
		position: "absolute",
		top: 0,
		bottom: 0,
		left: LIGNE_X,
		borderLeft: TRAIT,
	},
	// ├ — la partie horizontale (le trait vertical est posé par `traverse`).
	horizontal: {
		position: "absolute",
		top: "50%",
		left: LIGNE_X,
		right: 2,
		borderTop: TRAIT,
	},
	// └ — dernier enfant : coude arrondi, le trait s'arrête à mi-hauteur.
	coude: {
		position: "absolute",
		top: 0,
		height: "50%",
		left: LIGNE_X,
		right: 2,
		borderLeft: TRAIT,
		borderBottom: TRAIT,
		borderBottomLeftRadius: 8,
	},
	chevron: {
		width: 20,
		height: 20,
		padding: 0,
		border: "none",
		background: "transparent",
		cursor: "pointer",
		color: COLORS.textMuted,
		fontSize: 11,
		lineHeight: "20px",
		marginRight: 6,
		flexShrink: 0,
	},
	slotFeuille: {
		width: 20,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		marginRight: 6,
		flexShrink: 0,
	},
	pointFeuille: {
		width: 5,
		height: 5,
		borderRadius: 99,
		backgroundColor: COLORS.borderStrong,
	},
	replieCount: { marginLeft: 8, fontSize: 11, color: COLORS.textLight },
}

const Row = ({ children, index, onOpen, item }) => {
	const [hover, setHover] = useState(false)
	const bg = hover ? tableStyles.rowHover : tableStyles.rowBg(index)
	return (
		<tr
			onDoubleClick={() => onOpen(item)}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				backgroundColor: bg,
				cursor: "pointer",
				transition: "background-color 0.12s",
			}}
		>
			{children}
		</tr>
	)
}

// Libellé de l'action terrain d'une feuille (flags posés côté mobile à la
// sélection du diagnostic).
export const actionLabel = (diag) => {
	if (diag.is_sur_place) return "Sur place"
	if (diag.is_a_vide) return "Course à vide"
	if (diag.is_annule) return "Dépannage annulé"
	if (diag.show_articles) return "Articles"
	return ""
}

// Sous-onglet "Diagnostics assistance" (Configuration → Dépannage) — l'arbre
// des diagnostics du flux mobile (menus rapides, catégories, feuilles) avec
// leur action, les tarifs négociés par assistance et le tarif de base (repli
// quand l'assistance de la mission n'a pas de tarif configuré).
const DiagnostiquesV2 = () => {
	const dispatch = useDispatch()
	const tree = useSelector((s) => s.diagnostiqueInfo.tree)
	const tarifs = useSelector((s) => s.diagnostiqueInfo.tarifs)
	const fetchState = useSelector((s) => s.diagnostiqueInfo.fetchDiagnostiques)
	const [editing, setEditing] = useState(null)
	// Ids des catégories repliées (leurs descendants sont masqués).
	const [replies, setReplies] = useState(() => new Set())

	useEffect(() => {
		dispatch(fetchDiagnostiques())
		dispatch(fetchDiagTarifs())
		// Les assistances (donneurs d'ordre) alimentent la saisie des tarifs.
		dispatch(fetchAutreEntites())
	}, [dispatch])

	const enfantsDe = (id) => tree[id] || tree[String(id)] || []
	const nbTarifs = (diag) => Object.keys(tarifs[diag.id] || {}).length
	const compterDescendants = (id) =>
		enfantsDe(id).reduce((n, e) => n + 1 + compterDescendants(e.id), 0)

	const basculer = (id) =>
		setReplies((prev) => {
			const suivant = new Set(prev)
			if (suivant.has(id)) {
				suivant.delete(id)
			} else {
				suivant.add(id)
			}
			return suivant
		})

	// Totaux sur l'arbre complet ("-1" = doublons des menus rapides), pour que
	// replier une catégorie ne change pas les compteurs.
	const tous = Object.entries(tree)
		.filter(([groupe]) => groupe !== "-1")
		.flatMap(([, diags]) => diags)
	const nbFeuilles = tous.filter((d) => d.is_leaf).length
	const idsCategories = tous.filter((d) => !d.is_leaf).map((d) => d.id)

	// Racines affichées : parent nul (groupe "0") + groupes orphelins, dont le
	// parent n'existe plus en base (ex-racine id 1 supprimée : les grandes
	// catégories pointent dessus). Le mobile entre par les menus rapides ("-1")
	// et ne s'en aperçoit pas — ici on veut l'arbre entier.
	const idsExistants = new Set(tous.map((d) => d.id))
	const racines = [
		...(tree["0"] || []),
		...Object.entries(tree)
			.filter(
				([groupe]) =>
					groupe !== "-1" &&
					groupe !== "0" &&
					!idsExistants.has(Number(groupe)),
			)
			.flatMap(([, diags]) => diags),
	]

	// Aplatit l'arbre en lignes visibles. `guides` dit, pour chaque niveau
	// d'ancêtre, si un trait vertical traverse la ligne (l'ancêtre a encore
	// des frères plus bas) ; `isLast` choisit ├ ou └ pour le niveau du nœud.
	const lignes = []
	const empiler = (diag, niveau, guides, isLast) => {
		lignes.push({ diag, niveau, guides, isLast })
		if (replies.has(diag.id)) {
			return
		}
		const enfants = enfantsDe(diag.id)
		const guidesEnfants = niveau === 0 ? [] : [...guides, !isLast]
		enfants.forEach((e, i) =>
			empiler(e, niveau + 1, guidesEnfants, i === enfants.length - 1),
		)
	}
	racines.forEach((r, i) => empiler(r, 0, [], i === racines.length - 1))

	const renderCellNom = ({ diag, niveau, guides, isLast }) => {
		const replie = replies.has(diag.id)
		return (
			<td style={styles.arbreCell}>
				<div style={styles.arbreLigne}>
					{niveau > 0 && (
						<>
							{guides.map((traverse, i) => (
								<span key={i} style={styles.unit}>
									{traverse && (
										<span style={styles.traverse} />
									)}
								</span>
							))}
							<span style={styles.unit}>
								{isLast ? (
									<span style={styles.coude} />
								) : (
									<>
										<span style={styles.traverse} />
										<span style={styles.horizontal} />
									</>
								)}
							</span>
						</>
					)}
					{diag.is_leaf ? (
						<span style={styles.slotFeuille}>
							<span style={styles.pointFeuille} />
						</span>
					) : (
						<button
							style={styles.chevron}
							title={replie ? "Déplier" : "Replier"}
							onClick={(e) => {
								e.stopPropagation()
								basculer(diag.id)
							}}
							onDoubleClick={(e) => e.stopPropagation()}
						>
							<span
								style={{
									display: "inline-block",
									transition: "transform 0.15s",
									transform: replie
										? "rotate(-90deg)"
										: "none",
								}}
							>
								▾
							</span>
						</button>
					)}
					<span
						style={
							niveau === 0
								? styles.racine
								: diag.is_leaf
									? undefined
									: styles.categorie
						}
					>
						{diag.nom}
					</span>
					{diag.is_menu && (
						<span style={styles.badge}>menu rapide</span>
					)}
					{diag.show_articles && (
						<span style={styles.badgeArticles}>articles</span>
					)}
					{diag.is_sur_place && (
						<span style={styles.badgeSurPlace}>sur place</span>
					)}
					{replie && (
						<span style={styles.replieCount}>
							{compterDescendants(diag.id)} masqué(s)
						</span>
					)}
				</div>
			</td>
		)
	}

	return (
		<div>
			<div style={styles.header}>
				<div>
					<div style={styles.title}>Arbre des diagnostics</div>
					<div style={styles.count}>
						{tous.length} diagnostic(s), dont {nbFeuilles}{" "}
						feuille(s)
					</div>
				</div>
				<div style={styles.actions}>
					<button
						style={styles.btnPlier}
						onClick={() =>
							setReplies(
								replies.size
									? new Set()
									: new Set(idsCategories),
							)
						}
					>
						{replies.size ? "Tout déplier" : "Tout replier"}
					</button>
					<button
						style={gs.btnPrimary}
						onClick={() => setEditing({})}
					>
						+ Nouveau diagnostic
					</button>
				</div>
			</div>

			{fetchState.fetching && lignes.length === 0 ? (
				<Loader variant="ring" message="Chargement des diagnostics…" />
			) : lignes.length === 0 ? (
				<div style={styles.empty}>
					Aucun diagnostic — créez les catégories puis leurs feuilles.
				</div>
			) : (
				<div style={tableStyles.container}>
					<table style={tableStyles.table}>
						<thead>
							<tr>
								<th style={tableStyles.th}>Nom</th>
								<th style={tableStyles.th}>Type</th>
								<th style={tableStyles.th}>Action</th>
								<th style={tableStyles.th}>Tarif de base</th>
								<th style={tableStyles.th}>
									Tarifs assistance
								</th>
							</tr>
						</thead>
						<tbody>
							{lignes.map((ligne, index) => (
								<Row
									key={ligne.diag.id}
									index={index}
									item={ligne.diag}
									onOpen={setEditing}
								>
									{renderCellNom(ligne)}
									<td style={tableStyles.td}>
										{ligne.diag.is_leaf
											? "Feuille"
											: "Catégorie"}
									</td>
									<td style={tableStyles.td}>
										{actionLabel(ligne.diag)}
									</td>
									<td style={tableStyles.td}>
										{ligne.diag.is_leaf &&
										ligne.diag.tarif_base
											? `${ligne.diag.tarif_base.toFixed(2)} CHF`
											: ""}
									</td>
									<td style={tableStyles.td}>
										{ligne.diag.is_leaf &&
										nbTarifs(ligne.diag) > 0
											? nbTarifs(ligne.diag)
											: ""}
									</td>
								</Row>
							))}
						</tbody>
					</table>
				</div>
			)}

			{editing !== null && (
				<DiagnostiqueModalV2
					diag={editing}
					onClose={() => setEditing(null)}
				/>
			)}
		</div>
	)
}

export default DiagnostiquesV2
